import { useState, useRef, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { LuChevronLeft } from 'react-icons/lu';
import { MdFolder } from 'react-icons/md';
import type { AgentApiCandidateFormsShow } from '@/types/serializers';
import Input from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Button } from '@/components/Button';
import Select from '@/components/Select';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import useUpdateCandidateForm from '../api/useUpdateCandidateForm';
import useFetchCandidateForm from '../api/useFetchCandidateForm';

interface Props {
  candidateForm: AgentApiCandidateFormsShow;
}

export default function FormSettings({ candidateForm }: Props) {
  const { refetch } = useFetchCandidateForm();
  const { mutateAsync: updateForm, isPending } = useUpdateCandidateForm();
  const [formTitle, setFormTitle] = useState(candidateForm.title);
  const [status, setStatus] = useState(candidateForm.status);
  const [description, setDescription] = useState(candidateForm.description || '');
  const [termsOfServiceUrl, setTermsOfServiceUrl] = useState(
    candidateForm.terms_of_service_url || ''
  );
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState(candidateForm.privacy_policy_url || '');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(candidateForm.logo_url || null);
  const [colorType, setColorType] = useState<
    'white' | 'purple' | 'blue' | 'lightblue' | 'green' | 'black' | 'orange'
  >(candidateForm.color_type);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const statusOptions = [
    { value: 'draft', label: '下書き' },
    { value: 'published', label: '公開中' },
  ];

  // logoFileが変更されたとき、プレビューを更新
  useEffect(() => {
    if (logoFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(logoFile);
    }
  }, [logoFile]);

  const handleFileSelect = (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      showErrorToast('5MB以下の画像ファイルをアップロードしてください。');
      return;
    }

    if (!file.type.startsWith('image/')) {
      showErrorToast('画像ファイルをアップロードしてください。');
      return;
    }

    setLogoFile(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleChangeFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    try {
      await updateForm({
        title: formTitle,
        description,
        status,
        terms_of_service_url: termsOfServiceUrl || undefined,
        privacy_policy_url: privacyPolicyUrl || undefined,
        logo: logoFile || undefined,
        color_type: colorType,
      });
      await refetch();
      showSuccessToast('保存しました');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '保存に失敗しました';
      showErrorToast(errorMessage);
    }
  };

  return (
    <div className="space-y-6">
      {/* ホームへ戻る */}
      <Link
        to=".."
        className="mt-3 flex w-[300px] items-center gap-1 text-primary hover:opacity-60"
      >
        <LuChevronLeft className="size-4" />
        <span className="text-sm font-bold">ホームに戻る</span>
      </Link>

      {/* 基本情報セクション */}
      <div className="rounded-sm border border-solid border-gray-100 bg-white shadow-xs">
        <div className="p-6">
          <div className="space-y-6">
            {/* ステータス */}
            <div>
              <label htmlFor="status" className="mb-2 block text-sm font-bold text-main">
                フォームのステータス
              </label>
              <div className="w-full max-w-md">
                <Select
                  id="status"
                  items={statusOptions}
                  value={status}
                  onValueChange={(value) => setStatus(value as 'draft' | 'published')}
                  className="w-full"
                />
              </div>
            </div>

            {/* フォームタイトル */}
            <div>
              <label htmlFor="formTitle" className="mb-2 block text-sm font-bold text-main">
                フォームタイトル
              </label>
              <Input
                id="formTitle"
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                className="min-w-[500px]"
              />
              <p className="mt-2 text-xs text-gray-500">30文字以内</p>
            </div>

            {/* 説明文 */}
            <div>
              <label htmlFor="description" className="mb-2 block text-sm font-bold text-main">
                説明文
              </label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full"
                rows={8}
              />
              <p className="mt-2 text-xs text-gray-500">200文字以内</p>
            </div>

            {/* ロゴ設定 */}
            <div>
              <label className="mb-2 block text-sm font-bold text-main">ロゴ</label>
              <div className="flex flex-col gap-4">
                {/* ロゴプレビュー */}
                {logoPreview && (
                  <div className="flex flex-col">
                    <div className="flex min-h-[200px] justify-start rounded-sm p-4">
                      <img
                        src={logoPreview}
                        alt="ロゴプレビュー"
                        className="max-h-[180px] w-auto object-contain"
                      />
                    </div>
                  </div>
                )}

                {/* アップロードボタン */}
                <div>
                  {logoPreview ? (
                    <Button
                      type="button"
                      color="secondary"
                      size="md"
                      onClick={handleChangeFileClick}
                      className="gap-2"
                    >
                      <MdFolder className="size-5" />
                      <span>ファイルを変更する</span>
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      color="secondary"
                      size="md"
                      onClick={handleSelectFileClick}
                      className="gap-2"
                    >
                      <MdFolder className="size-5" />
                      <span>ファイルを選択</span>
                    </Button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    5MB以下の画像ファイルをアップロードしてください。
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="formThemaColor" className="mb-2 block text-sm font-bold text-main">
                テーマカラー
              </label>

              <div className="flex gap-6">
                {/* プレビュー画像 */}
                <div className="shrink-0">
                  <img
                    src={`/agent/candidate_forms/color-type-${colorType}.png`}
                    alt="テーマカラープレビュー"
                    className="max-h-[200px] w-auto object-contain"
                  />
                </div>

                {/* 色選択UI */}
                <div className="flex flex-1 flex-col">
                  <div className="grid grid-cols-4 gap-4">
                    {[
                      { value: 'blue', label: 'ネイビー', color: '#1e3a5f' },
                      { value: 'white', label: 'ホワイト', color: '#f5f5f5' },
                      { value: 'black', label: 'ブラック', color: '#000000' },
                      { value: 'lightblue', label: 'エメラルド', color: '#50c878' },
                      { value: 'green', label: 'グリーン', color: '#228b22' },
                      { value: 'purple', label: 'パープル', color: '#9370db' },
                      { value: 'orange', label: 'オレンジ', color: '#ff8c00' },
                    ].map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setColorType(color.value as typeof colorType)}
                        className={`flex flex-col items-center gap-2 rounded-sm border-1 border-solid p-3 transition-all hover:opacity-80 ${
                          colorType === color.value
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-100 bg-white'
                        }`}
                      >
                        <div
                          className="size-12 rounded-full border border-gray-300"
                          style={{
                            backgroundColor: color.color,
                          }}
                        />
                        <span className="text-xs font-medium text-gray-700">{color.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="formTos" className="mb-2 block text-sm font-bold text-main">
                利用規約URL
              </label>
              <Input
                id="formTos"
                type="text"
                value={termsOfServiceUrl}
                onChange={(e) => setTermsOfServiceUrl(e.target.value)}
                className="min-w-[500px]"
              />
              <p className="mt-2 text-xs text-gray-500">フォームのフッターに表示されます</p>
            </div>
            <div>
              <label htmlFor="formPrivacyPolicy" className="mb-2 block text-sm font-bold text-main">
                プライバシーポリシーURL
              </label>
              <Input
                id="formPrivacyPolicy"
                type="text"
                value={privacyPolicyUrl}
                onChange={(e) => setPrivacyPolicyUrl(e.target.value)}
                className="min-w-[500px]"
              />
              <p className="mt-2 text-xs text-gray-500">フォームのフッターに表示されます</p>
            </div>
          </div>
          {/* 保存ボタン */}
          <div className="mt-6 flex justify-end">
            <Button color="primary" size="lg" onClick={handleSave} disabled={isPending}>
              {isPending ? '保存中...' : '保存する'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
